import React from 'react';
import { MarketingJobStatus } from '../../domain/types';
import { Design, MarketingDesignExportJob } from '../../providers/api/dtos';

export interface MarketingDesignExportJobEntry {
  job: MarketingDesignExportJob,
  design: Design,
}

export interface AddJobResult {
  job: MarketingDesignExportJob,
  error?: Error,
}

export interface UpdateJobResult {
  job: MarketingDesignExportJob,
  error?: Error,
}

export interface MarketingDesignExportJobContextValue {
  jobs: MarketingDesignExportJobEntry[],
  addJob: (job: MarketingDesignExportJob, design: Design) => AddJobResult,
  updateJob: (job: MarketingDesignExportJob) => UpdateJobResult,
  removeJob: (id: string) => void,
  clearJobs: () => void,
  canAddJob: boolean,
  maxJobsInProgress: number,
}

export const MAX_MARKETING_DESIGN_EXPORT_JOBS = 3;

export const MarketingDesignExportJobContext = React.createContext<MarketingDesignExportJobContextValue>({
  jobs: [],
  addJob: (job) => ({ job }),
  updateJob: (job) => ({ job }),
  removeJob: () => {},
  clearJobs: () => {},
  canAddJob: true,
  maxJobsInProgress: MAX_MARKETING_DESIGN_EXPORT_JOBS,
});

interface State {
  jobs: Map<string, MarketingDesignExportJobEntry>;
}

type Action =
  | { type: 'START'; job: MarketingDesignExportJob; design: Design }
  | { type: 'UPDATE'; job: MarketingDesignExportJob }
  | { type: 'REMOVE'; id: string }
  | { type: 'CLEAR' };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'START': {
      const next = new Map(state.jobs);
      next.set(action.job.id, { job: action.job, design: action.design });
      return { jobs: next };
    }

    case 'UPDATE': {
      const existing = state.jobs.get(action.job.id);
      if (!existing) return state;
      const next = new Map(state.jobs);
      next.set(action.job.id, { ...existing, job: action.job });
      return { jobs: next };
    }

    case 'REMOVE': {
      const next = new Map(state.jobs);
      next.delete(action.id);
      return { jobs: next };
    }

    case 'CLEAR':
      return { jobs: new Map() };

    default:
      return state;
  }
};

export const useMarketingDesignExportJobContext = () => {
  const ctx = React.useContext(MarketingDesignExportJobContext);
  if (!ctx) {
    throw new Error('useMarketingDesignExportJobContext must be used within provider');
  }
  return ctx;
};

export const MarketingDesignExportJobContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = React.useReducer(reducer, { jobs: new Map() });

  /* -------- Derived state -------- */

  const jobsArray = React.useMemo(() => Array.from(state.jobs.values()), [state.jobs]);

  const inProgressCount = React.useMemo(
    () => Array.from(state.jobs.values()).filter((e) => e.job.status === MarketingJobStatus.IN_PROGRESS).length,
    [state.jobs],
  );

  const canAddJob = React.useMemo(
    () => inProgressCount < MAX_MARKETING_DESIGN_EXPORT_JOBS,
    [inProgressCount],
  );

  /* -------- Actions -------- */

  const addJob = React.useCallback(
    (job: MarketingDesignExportJob, design: Design): AddJobResult => {
      const isNew = !state.jobs.has(job.id);
      if (isNew && inProgressCount >= MAX_MARKETING_DESIGN_EXPORT_JOBS) {
        return {
          job,
          error: new Error(`Maximum number of in-progress export jobs (${MAX_MARKETING_DESIGN_EXPORT_JOBS}) reached`),
        };
      }
      dispatch({ type: 'START', job, design });
      return { job };
    },
    [state.jobs, inProgressCount],
  );

  const updateJob = React.useCallback(
    (job: MarketingDesignExportJob): UpdateJobResult => {
      if (!state.jobs.has(job.id)) {
        return { job, error: new Error(`Export job with ID ${job.id} not found`) };
      }
      dispatch({ type: 'UPDATE', job });
      return { job };
    },
    [state.jobs],
  );

  const removeJob = React.useCallback((id: string) => {
    dispatch({ type: 'REMOVE', id });
  }, []);

  const clearJobs = React.useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  /* -------- Context value -------- */

  const value = React.useMemo(
    () => ({
      jobs: jobsArray,
      addJob,
      updateJob,
      removeJob,
      clearJobs,
      canAddJob,
      maxJobsInProgress: MAX_MARKETING_DESIGN_EXPORT_JOBS,
    }),
    [jobsArray, addJob, updateJob, removeJob, clearJobs, canAddJob],
  );

  return (
    <MarketingDesignExportJobContext.Provider value={value}>
      {children}
    </MarketingDesignExportJobContext.Provider>
  );
};

export default MarketingDesignExportJobContextProvider;
